import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Download, RefreshCw, Share, SquarePlus, X } from 'lucide-react';
import { useT } from '@mosaferati/i18n';
import { Button, GlassSheet, Icon, springs, useIsStandalone } from '@mosaferati/ui';
import { useInstallPrompt, useServiceWorkerUpdate } from '@/lib/sw-register.ts';

const SNOOZE_KEY = 'mos.install-snoozed-at';
const SNOOZE_DAYS = 14;
const isIOS = () => typeof navigator !== 'undefined' && /iP(hone|ad|od)/.test(navigator.userAgent) && !/CriOS|FxiOS/.test(navigator.userAgent);

/** Update toast (new SW waiting) + install banner (Android) / iOS guide sheet. Mounted once in the root layout. */
export function InstallAndUpdate({ engaged }: { engaged: boolean }) {
  const t = useT();
  const sw = useServiceWorkerUpdate();
  const install = useInstallPrompt();
  const standalone = useIsStandalone();
  const [iosGuide, setIosGuide] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [snoozed] = useState(() => typeof localStorage !== 'undefined' && Date.now() - Number(localStorage.getItem(SNOOZE_KEY) ?? 0) < SNOOZE_DAYS * 86_400_000);
  const [installed] = useState(() => typeof localStorage !== 'undefined' && localStorage.getItem('mos.installed') === '1');
  const showInstall = engaged && !standalone && !dismissed && !snoozed && !installed && (install.canPrompt || isIOS());
  useEffect(() => {
    const onOpen = () => setIosGuide(true);
    window.addEventListener('mos:show-install-guide', onOpen);
    return () => window.removeEventListener('mos:show-install-guide', onOpen);
  }, []);
  const snooze = () => {
    localStorage.setItem(SNOOZE_KEY, String(Date.now()));
    setDismissed(true);
  };
  return (
    <>
      <AnimatePresence>
        {sw.available && (
          <motion.div key="update" initial={{ y: -80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -80, opacity: 0 }} transition={springs.default} className="pointer-events-none fixed inset-x-0 top-[calc(8px+env(safe-area-inset-top,0px))] z-[var(--m-z-toast)] flex justify-center px-4">
            <div className="glass-3 pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-xl px-4 py-3" role="status" data-testid="update-toast">
              <Icon icon={RefreshCw} size={18} className="text-tint" />
              <span className="min-w-0 flex-1 text-body font-semibold">{t('pwa.update.title')}</span>
              <Button size="sm" onClick={sw.apply}>{t('pwa.update.button')}</Button>
            </div>
          </motion.div>
        )}
        {showInstall && (
          <motion.div key="install" initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }} transition={springs.default} className="pointer-events-none fixed inset-x-0 bottom-[calc(96px+env(safe-area-inset-bottom,0px))] z-[var(--m-z-install)] flex justify-center px-4">
            <div className="glass-3 pointer-events-auto relative flex w-full max-w-md items-center gap-3 rounded-xl px-4 py-3" data-testid="install-banner">
              <img src="/icons/icon-192.png" alt="" className="h-11 w-11 rounded-lg" />
              <div className="min-w-0 flex-1">
                <div className="text-body font-semibold">{t('pwa.install.title')}</div>
                <div className="truncate text-caption text-text-secondary">{t('pwa.install.body')}</div>
              </div>
              <Button size="sm" onClick={() => (isIOS() ? setIosGuide(true) : void install.prompt().then((o) => o !== 'accepted' && snooze()))} leading={<Icon icon={Download} size={16} />}>{t('pwa.install.button')}</Button>
              <button type="button" aria-label={t('pwa.install.later')} onClick={snooze} className="absolute -end-1 -top-1 flex h-7 w-7 items-center justify-center rounded-full bg-bg-elevated text-text-tertiary shadow-1"><Icon icon={X} size={14} /></button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <GlassSheet open={iosGuide} onOpenChange={(o) => { setIosGuide(o); if (!o) snooze(); }} title={t('pwa.install.ios.title')} description={t('pwa.install.body')}>
        <ol className="flex flex-col gap-3 pb-3">
          {[
            { icon: Share, text: t('pwa.install.ios.step1') },
            { icon: SquarePlus, text: t('pwa.install.ios.step2') },
            { icon: Download, text: t('pwa.install.ios.step3') },
          ].map((s, i) => (
            <li key={i} className="flex items-center gap-3">
              <span className="m-num w-6 shrink-0 text-caption font-bold text-text-secondary">{i + 1}.</span>
              <Icon icon={s.icon} size={20} className="shrink-0 text-text-secondary" />
              <span className="text-body">{s.text}</span>
            </li>
          ))}
        </ol>
        <Button full variant="secondary" onClick={() => setIosGuide(false)}>{t('common.ok')}</Button>
      </GlassSheet>
    </>
  );
}
